import {
  EditMdIcon,
  OpenCalendarMdIcon,
  PostponeMdIcon,
  RedirectMdIcon,
  RemoveMdIcon,
} from '@crono-one/design-system';
import { Event } from 'crono-fe-common/types/event';
import { ComponentType, useMemo } from 'react';
import { getMeetingInfo, isMeetingCompleted } from '../utils/meeting.helpers';
import { useMeetingActionsContext } from './meetingActionsContext';

export type MeetingActionId =
  | 'join'
  | 'edit'
  | 'reschedule'
  | 'view-in-calendar'
  | 'delete';

export interface MeetingAction {
  id: MeetingActionId;
  label: string;
  Icon: ComponentType<{ className?: string }>;
  action: () => void;
}

interface MeetingActions {
  actions: MeetingAction[];
  joinAction: MeetingAction | null;
  isFutureMeeting: boolean;
  canEdit: boolean;
}

const NO_MEETING_ACTIONS: MeetingActions = {
  actions: [],
  joinAction: null,
  isFutureMeeting: false,
  canEdit: false,
};

// Shared meeting actions logic. Everything it depends on lives in
// MeetingActionsProvider, so the result only changes when the event or the
// user's calendars do — safe to call from a list row.
export const useMeetingActions = (event?: Event | null): MeetingActions => {
  const meetingActions = useMeetingActionsContext();

  return useMemo(() => {
    const meetingInfo = getMeetingInfo(event);
    if (!event || !meetingInfo) {
      return NO_MEETING_ACTIONS;
    }

    const {
      calendarEmails,
      openMeetingEditor,
      openMeetingReschedule,
      requestMeetingDelete,
    } = meetingActions;

    // Whether the meeting has a calendar event behind it (Google/Outlook).
    const isCalendarSynced = !!event.meeting;
    const isFutureMeeting = !isMeetingCompleted(meetingInfo);
    const isUserOrganizer = calendarEmails.includes(meetingInfo.organizerEmail);

    const canJoin = isFutureMeeting && !!meetingInfo.conferenceLink;
    const canEdit = !!event.owned || isUserOrganizer;
    const canReschedule = isCalendarSynced && canEdit;

    const joinAction: MeetingAction | null = canJoin
      ? {
          id: 'join',
          label: 'Join meeting',
          Icon: RedirectMdIcon,
          action: () => window.open(meetingInfo.conferenceLink!, '_blank'),
        }
      : null;

    const actions = [
      canEdit && {
        id: 'edit',
        label: 'Edit meeting',
        Icon: EditMdIcon,
        action: () => openMeetingEditor(event.id),
      },
      canReschedule && {
        id: 'reschedule',
        label: 'Reschedule meeting',
        Icon: PostponeMdIcon,
        action: () => openMeetingReschedule(meetingInfo),
      },
      meetingInfo.htmlLink && {
        id: 'view-in-calendar',
        label: 'View in calendar',
        Icon: OpenCalendarMdIcon,
        action: () => window.open(meetingInfo.htmlLink!, '_blank'),
      },
      canEdit && {
        id: 'delete',
        label: 'Delete meeting',
        Icon: RemoveMdIcon,
        action: () => requestMeetingDelete(event),
      },
    ].filter(Boolean) as MeetingAction[];

    return { actions, joinAction, isFutureMeeting, canEdit };
  }, [event, meetingActions]);
};
